import * as THREE from 'three';
import { Generator, GLSL_COMMON } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform float uLoopPhase;
  uniform vec2  uResolution;
  uniform vec3  uColorA;
  uniform vec3  uColorB;
  uniform vec3  uColorC;
  uniform float uSweep;
  uniform float uSoftness;
  uniform float uDarkness;
  uniform float uSpread;
  uniform float uGlow;
  uniform float uOffset;
  uniform float uBias;
  uniform float uMode;
  uniform float uTilt;
  uniform float uWipeSpeed;

  ${GLSL_COMMON}

  float beamMask(vec2 p, vec2 origin, float angle, float spread, float radius, float softness) {
    vec2 d = p - origin;
    float r = length(d);
    float a = atan(d.y, d.x);
    float diff = abs(atan(sin(a - angle), cos(a - angle)));
    float fan = smoothstep(spread, spread - softness, diff);
    float falloff = smoothstep(radius, radius * 0.1, r);
    return fan * falloff;
  }

  float diskMask(vec2 p, vec2 center, float radius, float softness) {
    float d = length(p - center);
    return smoothstep(radius, radius - softness, d);
  }

  float wipeBand(vec2 p, float pos, float width, float softness, float tilt) {
    float x = p.x + p.y * tilt;
    float left = smoothstep(pos - softness, pos + softness, x);
    float right = 1.0 - smoothstep(pos + width - softness, pos + width + softness, x);
    return left * right;
  }

  float easeOutBack(float t) {
    float c1 = 1.70158;
    float c3 = c1 + 1.0;
    float x = t - 1.0;
    return 1.0 + c3 * x * x * x + c1 * x * x;
  }

  void main() {
    vec2 uv = vUv;
    vec2 p = uv * 2.0 - 1.0;
    p.x *= uResolution.x / max(1.0, uResolution.y);
    float aspect = uResolution.x / max(1.0, uResolution.y);

    float phase = uLoopPhase * 6.28318530718;
    float sweep = sin(phase) * uSweep;

    vec2 o1 = vec2(-aspect * 0.92,  0.70 + sin(phase * 0.5) * uOffset);
    vec2 o2 = vec2(-aspect * 0.36, -0.12 + sin(phase + 1.8) * uOffset * 0.9);
    vec2 o3 = vec2( aspect * 0.18,  0.62 + cos(phase * 0.75) * uOffset * 0.7);
    vec2 o4 = vec2( aspect * 0.76, -0.18 + sin(phase * 0.65 + 0.7) * uOffset * 0.8);

    float a1 = 0.12 + sweep;
    float a2 = 1.05 - sweep * 0.6;
    float a3 = 2.45 + sin(phase * 0.5 + 2.4) * uSweep * 0.45;
    float a4 = 3.14159 - sweep * 0.8;

    float radius = max(2.6, aspect * 1.25);
    float b1 = beamMask(p, o1, a1, uSpread * 0.95, radius, uSoftness);
    float b2 = beamMask(p, o2, a2, uSpread * 0.82, radius * 1.05, uSoftness);
    float b3 = beamMask(p, o3, a3, uSpread * 0.72, radius * 0.95, uSoftness);
    float b4 = beamMask(p, o4, a4, uSpread * 0.92, radius * 1.08, uSoftness);

    float d1 = diskMask(p, o1 + vec2(-0.22, 0.0), 1.55, 0.20);
    float d2 = diskMask(p, o2 + vec2( 0.20, 0.0), 1.45, 0.18);
    float d3 = diskMask(p, o3 + vec2( 0.00, 0.15), 1.25, 0.16);
    float d4 = diskMask(p, o4 + vec2( 0.18, 0.0), 1.60, 0.22);

    float shutter1 = beamMask(p, o1, a1 + uBias, uSpread * 0.20, radius * 1.02, uSoftness * 0.75);
    float shutter2 = beamMask(p, o2, a2 - uBias * 0.8, uSpread * 0.18, radius * 1.04, uSoftness * 0.70);
    float shutter3 = beamMask(p, o3, a3 + uBias * 0.65, uSpread * 0.15, radius * 0.92, uSoftness * 0.70);
    float shutter4 = beamMask(p, o4, a4 - uBias * 0.9, uSpread * 0.19, radius * 1.08, uSoftness * 0.72);

    vec3 fanColor = vec3(0.01, 0.005, 0.02);
    fanColor += uColorA * b1 * (0.35 + d1 * 0.9) * uGlow;
    fanColor += uColorB * b2 * (0.30 + d2 * 1.0) * uGlow;
    fanColor += uColorC * b3 * (0.25 + d3 * 0.85) * uGlow;
    fanColor += mix(uColorA, uColorB, 0.45) * b4 * (0.28 + d4 * 0.95) * uGlow * 0.95;

    float fanDark = max(max(shutter1, shutter2), max(shutter3, shutter4)) * uDarkness;
    fanColor *= 1.0 - fanDark;

    float wipeSpan = aspect * 2.0;
    float cycles = max(0.5, uWipeSpeed) * 2.0;
    float cyclePhase = fract(uLoopPhase * cycles);
    float beatPulse = sin(uLoopPhase * 6.28318530718 * cycles);
    float stagger = sin(uLoopPhase * 6.28318530718 * (cycles * 0.5) + 1.1) * 0.04 * wipeSpan;
    float leadBase = -aspect - wipeSpan * 0.36 + easeOutBack(cyclePhase) * (wipeSpan * 1.42);
    float lead = leadBase + beatPulse * 0.035 * wipeSpan + stagger;
    float lead2 = lead - wipeSpan * (0.28 + 0.06 * sin(phase * 1.7 + 0.9));
    float lead3 = lead - wipeSpan * (0.63 + 0.08 * cos(phase * 1.25 - 1.2));
    float lead4 = lead - wipeSpan * (0.94 + 0.05 * sin(phase * 2.2 + 0.3));

    float w1 = wipeBand(p, lead, wipeSpan * uSpread * 0.55, uSoftness * 1.25,  uTilt);
    float w2 = wipeBand(p, lead2, wipeSpan * uSpread * 0.70, uSoftness * 1.4,  -uTilt * 0.7);
    float w3 = wipeBand(p, lead3, wipeSpan * uSpread * 0.48, uSoftness * 1.1,   uTilt * 1.15);
    float w4 = wipeBand(p, lead4, wipeSpan * uSpread * 0.32, uSoftness * 0.95,  -uTilt * 1.35);

    float ws1 = wipeBand(p, lead + wipeSpan * 0.18, wipeSpan * uSpread * 0.14, uSoftness * 0.75,  uTilt * 1.1);
    float ws2 = wipeBand(p, lead2 + wipeSpan * 0.24, wipeSpan * uSpread * 0.12, uSoftness * 0.70, -uTilt * 0.9);
    float ws3 = wipeBand(p, lead3 + wipeSpan * 0.20, wipeSpan * uSpread * 0.10, uSoftness * 0.65,  uTilt * 1.25);
    float ws4 = wipeBand(p, lead4 + wipeSpan * 0.16, wipeSpan * uSpread * 0.08, uSoftness * 0.60, -uTilt * 1.4);

    vec3 wipeColor = vec3(0.008, 0.004, 0.018);
    wipeColor += uColorA * w1 * (0.72 + 0.28 * sin(phase + p.y * 2.0 + cyclePhase * 6.28318)) * uGlow;
    wipeColor += uColorB * w2 * (0.76 + 0.24 * cos(phase * 1.4 - p.y * 1.7 + cyclePhase * 4.6)) * uGlow;
    wipeColor += uColorC * w3 * (0.80 + 0.20 * sin(phase * 0.9 + p.y * 2.7 - cyclePhase * 5.4)) * uGlow;
    wipeColor += mix(uColorA, uColorC, 0.5) * w4 * (0.64 + 0.36 * sin(phase * 2.3 + p.y * 3.4)) * uGlow * 0.82;
    wipeColor += mix(uColorA, uColorB, 0.55) * (w1 * w2) * uGlow * 0.45;
    wipeColor += mix(uColorB, uColorC, 0.45) * (w2 * w3) * uGlow * 0.4;
    wipeColor += mix(uColorA, uColorB, 0.25) * (w3 * w4) * uGlow * 0.32;

    float wipeDark = max(max(ws1, ws2), max(ws3, ws4)) * uDarkness;
    wipeColor *= 1.0 - wipeDark;

    float wipeVignette = smoothstep(aspect * 1.25, 0.35, length(p * vec2(0.35, 0.95)));
    wipeColor *= wipeVignette;

    vec3 color = fanColor;
    if (uMode > 0.5 && uMode < 1.5) color = wipeColor;
    else if (uMode >= 1.5) color = mix(fanColor, wipeColor, 0.6);

    gl_FragColor = vec4(color, 1.0);
  }
`;

const PALETTES = {
  mint: ['#5af0d1', '#a7ef9c', '#27352f'],
  club: ['#f10869', '#8b0f4c', '#100611'],
  dusk: ['#cbb6ff', '#4b4b8f', '#0b0912'],
};

export class LightFans extends Generator {
  constructor() {
    super({
      name: 'Light Fans',
      category: 'ambient',
      schema: {
        mode:     { default: 'fan', type: 'select', label: 'Mode',
                    options: [['fan', 'Fan'], ['wipe', 'Wipe'], ['hybrid', 'Hybrid']] },
        palette:  { default: 'mint', type: 'select', label: 'Palette',
                    options: [['mint', 'Mint'], ['club', 'Club'], ['dusk', 'Dusk']] },
        sweep:    { default: 0.42, min: 0.05, max: 1.2, step: 0.01, label: 'Sweep' },
        spread:   { default: 0.95, min: 0.15, max: 1.6, step: 0.01, label: 'Spread' },
        softness: { default: 0.22, min: 0.02, max: 0.8, step: 0.01, label: 'Softness' },
        glow:     { default: 1.25, min: 0.1, max: 3.0, step: 0.01, label: 'Glow' },
        darkness: { default: 0.92, min: 0.0, max: 1.0, step: 0.01, label: 'Shutter dark' },
        offset:   { default: 0.18, min: 0.0, max: 0.6, step: 0.01, label: 'Offset drift' },
        bias:     { default: 0.42, min: 0.02, max: 1.2, step: 0.01, label: 'Shutter bias' },
        tilt:     { default: 0.22, min: -1.0, max: 1.0, step: 0.01, label: 'Wipe tilt' },
        wipeSpeed:{ default: 1.0, min: 0.25, max: 4.0, step: 0.01, label: 'Wipe speed' },
      }
    });
  }

  onEnter(scene, engine) {
    const palette = PALETTES[this.v('palette')];
    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uLoopPhase:  { value: 0 },
        uResolution: { value: new THREE.Vector2(engine.width, engine.height) },
        uColorA:     { value: new THREE.Color(palette[0]) },
        uColorB:     { value: new THREE.Color(palette[1]) },
        uColorC:     { value: new THREE.Color(palette[2]) },
        uSweep:      { value: this.v('sweep') },
        uSoftness:   { value: this.v('softness') },
        uDarkness:   { value: this.v('darkness') },
        uSpread:     { value: this.v('spread') },
        uGlow:       { value: this.v('glow') },
        uOffset:     { value: this.v('offset') },
        uBias:       { value: this.v('bias') },
        uMode:       { value: this.v('mode') === 'wipe' ? 1 : this.v('mode') === 'hybrid' ? 2 : 0 },
        uTilt:       { value: this.v('tilt') },
        uWipeSpeed:  { value: this.v('wipeSpeed') },
      },
      transparent: false,
      depthWrite: false,
      depthTest: false,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
  }

  onResize(w, h) {
    if (this.mesh) this.mesh.material.uniforms.uResolution.value.set(w, h);
  }

  onParamChange(key, val) {
    if (!this.mesh) return;
    const u = this.mesh.material.uniforms;
    if (key === 'palette') {
      const palette = PALETTES[val];
      u.uColorA.value.set(palette[0]);
      u.uColorB.value.set(palette[1]);
      u.uColorC.value.set(palette[2]);
      return;
    }
    if (key === 'mode') {
      u.uMode.value = val === 'wipe' ? 1 : val === 'hybrid' ? 2 : 0;
      return;
    }
    const uniformKey = `u${key[0].toUpperCase()}${key.slice(1)}`;
    if (uniformKey in u) u[uniformKey].value = val;
  }

  update(dt, time, engine) {
    if (!this.mesh) return;
    this.mesh.material.uniforms.uLoopPhase.value = engine.getLoopInfo(time).phase;
  }
}
